import type { Options } from "@tradingApp/components/Trading/AssetChart/types";
import {
  applyContextOptions,
  forceRestoreContextOptions,
  restoreContextOptions,
} from "@tradingApp/helpers/canvas/contextOptions";
import {
  applyTranslate,
  restoreTranslate,
} from "@tradingApp/helpers/canvas/translate";
import { getFirstAndLastItemsFromArray } from "@tradingApp/utils/array/getFirstAndLastItems";
import { getDevicePixelRatio } from "@tradingApp/utils/getDevicePixelRatio";
import { type Line, line } from "d3";

export function tradingAssetChartDrawLayer(
  options: Options,
  context: CanvasRenderingContext2D,
): void {
  const { xScale, yScale, dates, data } = options;

  const lineGenerator = line<number>()
    .x((_, i) => xScale(dates[i]))
    .y((d) => yScale(d))
    .context(context);

  applyContextOptions({
    context,
    contextOptions: {
      modify: (ctx) => {
        ctx.scale(getDevicePixelRatio(), getDevicePixelRatio());
      },
    },
  });

  applyTranslate({ ...options, context });
  applyContextOptions({ ...options, context });
  regularChartDrawGradient(options, context, lineGenerator);
  context.beginPath();
  lineGenerator(data);
  context.stroke();
  context.closePath();
  restoreContextOptions({ ...options, context });
  restoreTranslate({ ...options, context });
  forceRestoreContextOptions(context);
}

export function regularChartDrawGradient(
  options: Options,
  context: CanvasRenderingContext2D,
  lineGenerator: Line<number>,
): void {
  const { xScale, yScale, data, dates, gradientColors } = options;

  const [startColor, endColor] = gradientColors ?? [
    "transparent",
    "transparent",
  ];

  const [firstData] = getFirstAndLastItemsFromArray(data);
  const [, lastDate] = getFirstAndLastItemsFromArray(dates);
  const [yEnd] = yScale.range();
  const gradient = context.createLinearGradient(0, 0, 0, yEnd);
  applyTranslate({ ...options, context });
  context.beginPath();
  context.strokeStyle = "transparent";
  gradient.addColorStop(0, startColor);
  gradient.addColorStop(1, endColor);
  context.fillStyle = gradient;
  lineGenerator(data);

  if (lastDate) {
    context.lineTo(xScale(lastDate), yEnd);
  }

  context.lineTo(0, yEnd);

  if (firstData) {
    context.lineTo(0, yScale(firstData));
  }

  context.closePath();
  context.stroke();
  context.fill();
  restoreTranslate({ ...options, context });
}
